import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link, Routes, Route, Navigate } from "react-router-dom";
import { adminColors, TOUCH_HIT_SX, FOCUS_RING_ON_DARK, radius } from './theme/adminTheme';
import { getInitials } from './utils/userDisplay';
import { alpha } from '@mui/material/styles';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Avatar,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Select,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Tooltip,
  Checkbox,
  Tab,
  Tabs,
  LinearProgress,
  Alert,
  Snackbar,
  Skeleton,
  FormControlLabel
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Badge as BadgeIcon,
  School as SchoolIcon,
  Menu as MenuIcon,
  Chat as ChatIcon,
  MenuBook as MenuBookIcon,
  LibraryBooks as LibraryBooksIcon,
  Logout as LogoutIcon,
  Security as SecurityIcon,
  HowToReg as HowToRegIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SmartToy as SmartToyIcon,
  Domain as DomainIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import config, { AGENT_TUTOR_COMING_SOON } from "./config";
import { authFetch, handleApiResponse, describeApiError, ApiError, clearToken } from "./api/authFetch";
import { describeAdminError } from "./utils/adminErrors";
import SafetyFlagsView from "./components/admin/SafetyFlagsView";
import ConversationViewer from "./components/admin/ConversationViewer";
import CurriculumPage from "./components/admin/CurriculumPage";
import AgentTutorView from "./components/admin/agentTutor/AgentTutorView";
import AgentTutorComingSoon from "./components/agentTutor/AgentTutorComingSoon";
import SchoolManagement from "./components/admin/SchoolManagement";
// UsersView.js itself is deliberately left in the tree (see the phase-1
// build note — a later cleanup pass removes it) but is no longer imported
// here: /admin/students now routes to StudentsPage below.
import StudentsPage from "./components/admin/StudentsPage";
import StaffPage from "./components/admin/StaffPage";
import ClassesPage from "./components/admin/ClassesPage";
import OverviewPage from "./components/admin/OverviewPage";
import SafetyReviewPage from "./components/admin/SafetyReviewPage";
// InvitesPage merges the old separately-navigable InvitationsView.js
// (Email Invites) and AccessRequestsView.js (Sign-up Requests) into one
// funnel (admin console v2 Phase 2) — those two files are still in the
// tree but no longer imported anywhere; see InvitesPage.js's own header
// comment.
import InvitesPage from "./components/admin/InvitesPage";
import {
  ROLES,
  ROLE_COLORS,
  ROLE_LABELS,
  isTeacher,
  isAdminRole,
  isMasterAdmin,
  isStaffRole,
  isSchoolScopedRole,
  assignableRoles,
} from "./constants/roles";

const DRAWER_WIDTH_EXPANDED = 280;
// Icon-only rail width when an admin collapses the nav: a 44px touch
// target (TOUCH_HIT_SX) plus enough horizontal breathing room on each side
// for the icon and its focus ring not to look clipped.
const DRAWER_WIDTH_COLLAPSED = 76;

// Collapsed-nav is a per-device UI preference, not account data — plain
// localStorage (not the sessionStorage authFetch.js uses for the auth
// token) is correct here specifically because it's meant to survive
// reloads *and* isn't sensitive, unlike a session token on a shared
// school/lab computer. See src/api/authFetch.js for the sessionStorage
// reasoning this deliberately does not follow.
const NAV_COLLAPSED_STORAGE_KEY = 'admin_nav_collapsed';

function readNavCollapsedPreference() {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    // Storage disabled (private browsing, locked-down lab machine, etc.) —
    // default to expanded, same as a first-ever visit.
    return false;
  }
}

// Mirrors the backend's schemas.EMAIL_FORMAT_RE (app/schemas.py) for instant
// client-side feedback in the Add User dialog below (UsersView.js's own
// edit-email dialog keeps its own copy of this same regex, for the same
// reason). The frontend can't import a Python regex, so this is kept in
// sync by hand — the server's own validate_email_format() is still the
// actual source of truth, and its 400 ("Invalid email address.") is
// surfaced inline as a fallback wherever this pattern might drift or miss
// an edge case.
const EMAIL_FORMAT_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Toast notifications — replaces window.alert() for success/error feedback.
// Declared at module scope (not inside AdminDashboardV2) and owns its own
// {open, message, severity} state internally. AdminDashboardV2 talks to it
// via `apiRef` (a plain function ref) instead of via its own state, so
// firing a toast never triggers a re-render of AdminDashboardV2 — which
// would otherwise remount the inline view components (ClassesView,
// InvitationsView, AccessRequestsView, DashboardView, PlaceholderView) and
// wipe their local state. See notify() below.
const NotificationHost = ({ apiRef }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    apiRef.current = (message, severity = 'success') => {
      setSnackbar({ open: true, message, severity });
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef]);

  const closeSnackbar = (_event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Snackbar
      open={snackbar.open}
      // Error and warning toasts stay until dismissed — a missed 5s toast on
      // a failed delete/update reads as "nothing happened yet", and a
      // missed warning (e.g. "that was the class's last teacher") reads as
      // nothing being wrong when something now needs attention.
      autoHideDuration={snackbar.severity === 'error' || snackbar.severity === 'warning' ? null : 5000}
      onClose={closeSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={closeSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
};

// Generic destructive-action confirm dialog — replaces window.confirm() and
// the various one-off "are you sure" dialogs previously scattered through
// this file. Same pattern as NotificationHost: state lives here, not on
// AdminDashboardV2, so opening/closing it never remounts the active view.
// See confirmAction() below.
const ConfirmHost = ({ apiRef }) => {
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    body: '',
    confirmLabel: 'Confirm',
    onConfirm: null,
  });

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, title: '', body: '', confirmLabel: 'Confirm', onConfirm: null });
  };

  useEffect(() => {
    apiRef.current = ({ title, body, confirmLabel = 'Confirm', onConfirm }) => {
      setConfirmDialog({ open: true, title, body, confirmLabel, onConfirm });
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef]);

  // Guards against a fast double-click on the confirm button firing the
  // (often destructive) action twice - a ref rather than state since it
  // must be checked/set synchronously on the first click, before React has
  // a chance to re-render and disable the button.
  const confirmInFlightRef = useRef(false);

  const handleConfirmDialogConfirm = async () => {
    if (confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    try {
      if (action) await action();
    } finally {
      confirmInFlightRef.current = false;
    }
  };

  return (
    <Dialog
      open={confirmDialog.open}
      onClose={closeConfirmDialog}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title">
        {confirmDialog.title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {confirmDialog.body}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeConfirmDialog} color="primary">
          Cancel
        </Button>
        <Button onClick={handleConfirmDialogConfirm} color="error" variant="contained">
          {confirmDialog.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Maps every nav item's id (menuGroups above, renderRoutes' <Route> list
// below) to the URL segment it now lives at under /admin/*. This is
// the one place that translation lives — everything else (nav Link hrefs,
// the AppBar title lookup, the <Routes> below) derives from it rather than
// hardcoding the string twice and letting them drift.
//
// The dashboard/overview item is the index route (no segment, hence '').
// `users`/`staff`/`classes` accept an optional trailing :username / :groupId
// so a row can be deep-linked (`/admin/students/jdoe`, `/admin/staff/jdoe`,
// `/admin/classes/12`) into StudentsPage's/StaffPage's drawer or
// ClassesPage's detail pane. `staff` is admin-only (school admin + master
// admin, never a teacher) — gated both by the nav item below
// (isAdminRole) and, since hiding the link alone doesn't stop a direct URL,
// by the route itself in renderRoutes. `safety` is two separate routes
// rather than one optional-param route: `safety` is the triage list
// (SafetyFlagsView), `safety/:flagId` is the distinct review-in-context page
// (SafetyReviewPage) its "Review"/"Update" action navigates to — see
// renderRoutes below.
//
// `accessRequests: 'requests'` has no nav item pointing at it any more
// (admin console v2 Phase 2 merged Sign-up Requests into `invitations`'s
// InvitesPage — see the People group above) but stays mapped here so a
// stale bookmark/link at /admin/requests still resolves to a real
// `currentView` (and the right AppBar title) for the one render before its
// <Route> below redirects it to /admin/invites, rather than that URL
// falling through to VIEW_ROUTES' "unrecognized segment" default.
//
// `assignments: 'rosters'` is the same "stale bookmark, no nav item"
// situation: the standalone "Class Rosters" page (ClassUserAssignmentView)
// was removed once Classes & Schedules' own detail pane grew teachers/time-
// slots/roster management for a class in one place — see the `rosters`
// <Route> below, which redirects to /admin/classes.
const VIEW_ROUTES = {
  dashboard: '',
  users: 'students',
  staff: 'staff',
  classes: 'classes',
  assignments: 'rosters',
  invitations: 'invites',
  accessRequests: 'requests',
  safety: 'safety',
  conversations: 'conversations',
  agentTutor: 'agents',
  platformGuide: 'guide',
  curriculum: 'curriculum',
  schoolManagement: 'schools',
};

// Builds the absolute href for a nav item id, e.g. viewHref('safety') ->
// '/admin/safety'. viewHref('dashboard') -> '/admin' (not '/admin/', so it
// matches the index route's own canonical URL exactly).
const viewHref = (id) => {
  const segment = VIEW_ROUTES[id];
  return segment ? `/admin/${segment}` : '/admin';
};

function AdminDashboardV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentUser, setCurrentUser] = useState(null);
  // Derived from the URL (via VIEW_ROUTES above), not its own state — the
  // current view *is* the current route now, so there's exactly one source
  // of truth for "what's on screen" instead of a currentView useState that
  // could drift from the address bar. Only the path's first segment is
  // read; a nested optional param (e.g. /admin/students/jdoe) still resolves
  // to 'users', same as the bare /admin/students. Unrecognized first
  // segments (including none, i.e. bare '/admin') resolve to 'dashboard'.
  const activeSegment = location.pathname.replace(/^\/admin\/?/, '').split('/')[0] || '';
  const currentView =
    activeSegment === ''
      ? 'dashboard'
      : Object.keys(VIEW_ROUTES).find((id) => VIEW_ROUTES[id] === activeSegment) || 'dashboard';
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop-only "collapse the nav to reclaim width" preference (the wide
  // Users/Invitations tables are why this exists), persisted below. Read
  // synchronously in the initializer so the first paint already has the
  // right width instead of flashing expanded-then-collapsed.
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsedPreference);
  // Anchor for the collapsed rail's account menu (Chat / Sign Out) — kept
  // separate from any other menu state since it's the rail-only equivalent
  // of the expanded footer's hover-revealed buttons.
  const [railUserMenuAnchor, setRailUserMenuAnchor] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set when fetchDashboardData fails, so the UI can tell a real backend
  // failure apart from "you genuinely have no users/classes yet".
  const [dashboardError, setDashboardError] = useState(null);
  // Set when the initial /api/me lookup fails for a reason other than a real
  // 401 (network error, timeout, non-401 server error). handleApiResponse
  // already redirects to /signin for a genuine 401, so anything landing here
  // is retryable and should not bounce a signed-in admin out of the app.
  const [meError, setMeError] = useState(null);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [addUserDialogTab, setAddUserDialogTab] = useState(0);   // 0=single, 1=bulk
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  // Toast + confirm-dialog state live in NotificationHost/ConfirmHost (module
  // scope, below), not here — see notify()/confirmAction() below for why:
  // setting state on AdminDashboardV2 re-renders it, which remounts the
  // inline view components (ClassesView, InvitationsView, etc.) and wipes
  // their local state.
  const notifyApiRef = useRef(null);
  const confirmApiRef = useRef(null);
  // LEAR-26: single email invite state
  const [singleInviteData, setSingleInviteData] = useState({ email: '', role: 'STUDENT', school: '' });
  const [singleInviteSending, setSingleInviteSending] = useState(false);
  const [singleInviteResult, setSingleInviteResult] = useState(null); // { severity: 'success'|'warning'|'error', message }
  const [newUserData, setNewUserData] = useState({
    username: '',
    password: '',
    role: 'STUDENT',
    school: '',
    email: '',
    // Product decision: new users get an email by default so self-service
    // password reset works — this checkbox is the explicit escape hatch for
    // test/bulk accounts that genuinely have none. See the Email field's
    // `required`/disabled wiring in the Add User dialog below.
    noEmail: false
  });
  // Inline 400 from POST /api/users when it's specifically an email-format
  // rejection (mirrors validate_email_format's "Invalid email address.") —
  // shown on the Email field itself rather than only as a toast, since it's
  // the one field this dialog validates server-side beyond presence.
  const [newUserEmailError, setNewUserEmailError] = useState(null);

  // Shows a transient toast — replaces window.alert() for success/error
  // feedback. Delegates to NotificationHost via a ref so calling notify()
  // never sets state on AdminDashboardV2 itself (see notifyApiRef above).
  // Child views declared inside this function close over `notify` directly;
  // UsersView (declared outside, above) receives it as a prop.
  const notify = useCallback((message, severity = 'success') => {
    notifyApiRef.current?.(message, severity);
  }, []);

  // Opens the shared destructive-action confirm dialog — replaces
  // window.confirm() and the various one-off "are you sure" dialogs
  // previously scattered through this file. Delegates to ConfirmHost via a
  // ref for the same reason notify() delegates to NotificationHost.
  const confirmAction = useCallback(({ title, body, confirmLabel = 'Confirm', onConfirm }) => {
    confirmApiRef.current?.({ title, body, confirmLabel, onConfirm });
  }, []);

  // Pending safety flags, reused as the sidebar badge count below — same
  // /api/dashboard/stats value the "Active Safety Flags" dashboard tile
  // reads, not a second fetch.
  const pendingSafetyFlags = stats?.active_safety_flags ?? 0;

  // Keeps the sidebar badge + dashboard tile's `stats.active_safety_flags`
  // in sync after SafetyFlagsView reviews/dismisses a flag, without
  // refetching the rest of the dashboard (fetchDashboardData also re-fetches
  // /api/user-access and /api/groups, which have nothing to do with a
  // safety-flag review and would be wasted bandwidth on every review click).
  // Reuses the same GET /api/safety/flags?status=PENDING&page_size=1 request
  // SafetyFlagsView's own "Pending" tab count already makes — no new
  // endpoint — and only reads `total`, merging it into the existing `stats`
  // object so every other stat tile is untouched.
  //
  // This does re-render AdminDashboardV2 (setStats), which normally would
  // remount whichever *inline* view (DashboardView, ClassesView, etc. —
  // declared inside this function body, so they're redefined as new
  // function identities every render) is on screen. SafetyFlagsView is not
  // one of those: it's a module-level import (stable identity, see the
  // top-of-file import), rendered at the same <Route> element position in
  // renderRoutes() below, so React reconciles it as an update, not a
  // remount — its internal filters/pagination/open dialog survive.
  const refreshSafetyFlagsCount = useCallback(async () => {
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/safety/flags?status=PENDING&page_size=1`);
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      if (typeof data.total !== 'number') return;
      setStats((prev) => (prev ? { ...prev, active_safety_flags: data.total } : prev));
    } catch (error) {
      // Best-effort badge sync — a failure here just means the sidebar
      // count is briefly stale, not worth surfacing a toast for.
      console.error('Error refreshing safety flag count:', error);
    }
  }, [navigate]);

  // Sidebar navigation, grouped by task area (Miller's Law / common region)
  // instead of one flat 12-item list. Each item keeps the *exact* role-gating
  // condition the old flat list used — only the grouping/order changed, not
  // who sees what. A group renders nothing (see menuGroups.filter below) when
  // every one of its items is hidden for the current role, so no empty
  // section header ever shows.
  //
  // Revised per product-owner feedback: Classes & Schedules / Schools moved
  // out of Learning into their own "Schools & Classes" group; Learning now
  // holds Curriculum + Agent Tutor (Agent Tutor moved out of Resources);
  // Resources is intentionally left as a single-item group (Help & Guides
  // only).
  //
  // The standalone "Class Rosters" item/page (ClassUserAssignmentView) was
  // removed in a later cleanup once Classes & Schedules' own detail pane
  // (ClassesPage.js) grew teachers/time-slots/roster management for a class
  // in one place — see /admin/rosters' redirect in renderRoutes below.
  //
  // Schools stays isAdminRole (MASTER_ADMIN + SCHOOL_ADMIN), same route
  // either way, but the label and what's on the page differ by role — see
  // the `schoolManagement` item below and SchoolManagement.js's own header
  // comment.
  //
  // TEACHER is a SCHOOL_ADMIN scoped to their own classes, minus anything
  // that edits roles/schools/access or manages other admins/teachers — see
  // constants/roles.js. Concretely that means a teacher gets: Dashboard,
  // Safety, People (their students + inviting students, no sign-up
  // requests), Classes & Schedules (no Schools — a teacher doesn't manage
  // school settings and has no reason to browse other schools, so it's left
  // out entirely rather than shown read-only with nothing to do), Curriculum
  // + Agent Tutor, and Resources.
  const currentRole = currentUser?.role;
  const menuGroups = [
    {
      id: 'overview',
      label: 'Overview',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
      ],
    },
    // Safety group: MASTER_ADMIN, SCHOOL_ADMIN, or TEACHER (backend scopes
    // a teacher's results to their own classes' students).
    ...(isStaffRole(currentRole) ? [{
      id: 'safety-group',
      label: 'Safety',
      items: [
        { id: 'safety', label: 'Safety Monitoring', icon: <SecurityIcon />, badge: pendingSafetyFlags },
        { id: 'conversations', label: 'Conversation Viewer', icon: <ChatIcon /> },
      ],
    }] : []),
    {
      id: 'people',
      label: 'People',
      items: [
        // "Students" for every role now, not just a teacher — the old
        // combined "All Users" page (UsersView.js) was split into this
        // STUDENT-only page plus the admin-only Staff item just below as
        // part of the admin-console v2 staff split; the label needs to
        // actually say what the page shows now. See StudentsPage.js's own
        // header comment for the exact scoping matrix.
        { id: 'users', label: isTeacher(currentRole) ? 'My Students' : 'Students', icon: <PeopleIcon /> },
        // Staff (teachers + school/master admins) is admin-only — a
        // TEACHER never gets this item, not even read-only (see
        // VIEW_ROUTES's `staff` comment for the matching route-level
        // guard, since hiding the link alone wouldn't stop a direct URL).
        ...(isAdminRole(currentRole) ? [
          { id: 'staff', label: 'Staff', icon: <BadgeIcon /> },
        ] : []),
        // "Invites" (InvitesPage.js, admin console v2 Phase 2) merges what
        // used to be two separate nav items — Email Invites and Sign-up
        // Requests — into one funnel: a MASTER_ADMIN sees both halves on
        // that one page (sign-up requests are still a platform-wide inbox,
        // gated MASTER_ADMIN-only exactly as before, just inside the page
        // now rather than via a second nav item); every other STAFF_ROLES
        // member just sees their Invites half. One nav entry either way —
        // see InvitesPage.js's own header comment.
        ...(isStaffRole(currentRole) ? [
          { id: 'invitations', label: 'Invites', icon: <HowToRegIcon /> },
        ] : []),
      ],
    },
    {
      id: 'management',
      label: 'Schools & Classes',
      items: [
        { id: 'classes', label: 'Classes & Schedules', icon: <SchoolIcon /> },
        // School management (settings, not classes) stays MASTER_ADMIN /
        // SCHOOL_ADMIN / legacy "admin" only — a TEACHER never sees it, even
        // read-only, since there's nothing for them to do on that page (and
        // is refused route-level too, see the `schools` route in
        // renderRoutes below). The label itself tells the two admin roles
        // apart: a SCHOOL_ADMIN lands on the exact same route but
        // SchoolManagement.js scopes it to their own school only (no
        // catalog, no add/rename/merge — see that file's own header
        // comment), so "My School" reads honestly; a MASTER_ADMIN gets the
        // full multi-school catalog, so "Schools" (plural) still fits.
        ...(isAdminRole(currentRole) ? [
          {
            id: 'schoolManagement',
            label: isMasterAdmin(currentRole) ? 'Schools' : 'My School',
            icon: <DomainIcon />,
          },
        ] : []),
      ],
    },
    {
      id: 'learning',
      label: 'Learning',
      items: [
        // LibraryBooksIcon here (not MenuBookIcon) is deliberate: Resources'
        // "Help & Guides" below also uses a book glyph, and the label that
        // disambiguates them at full width disappears in the icon-only rail
        // — two identical book icons a divider apart would be indistinguishable
        // without hovering each one.
        { id: 'curriculum', label: 'Curriculum', icon: <LibraryBooksIcon /> },
        ...(isStaffRole(currentRole) ? [
          { id: 'agentTutor', label: 'Agent Tutor', icon: <SmartToyIcon /> },
        ] : []),
      ],
    },
    // Resources intentionally left as a single-item group (Help & Guides
    // only) — still needs its own top-level gate since, unlike Management/
    // Learning above, it has no always-visible item to keep it non-empty.
    ...(isStaffRole(currentRole) ? [{
      id: 'resources',
      label: 'Resources',
      items: [
        { id: 'platformGuide', label: 'Help & Guides', icon: <MenuBookIcon /> },
      ],
    }] : []),
  ].filter((group) => group.items.length > 0);

  // Flat view of the same items, kept for the two lookups that don't care
  // about grouping (AppBar title, the "Coming Soon" comment below).
  const menuItems = menuGroups.flatMap((group) => group.items);

  // Fetches the signed-in admin's profile. Runs on mount and is re-run by
  // the Retry action on the error screen below. handleApiResponse already
  // redirects to /signin on a genuine 401, so a failure reaching the catch
  // here is a network error, timeout, or non-401 server error — those should
  // show a retryable error, not force a sign-in redirect.
  const fetchCurrentUser = () => {
    setMeError(null);
    authFetch(`${config.API_BASE_URL}/api/me`)
      .then(res => handleApiResponse(res, navigate))
      .then(data => {
        if (data) {
          setCurrentUser(data);
          fetchDashboardData(data);
        } else {
          // 401 — handleApiResponse already redirected
          setLoading(false);
        }
      })
      .catch(error => {
        console.error('Error fetching user:', error);
        setLoading(false);
        setMeError(`Couldn't load your account. ${describeApiError(error)}`);
      });
  };

  useEffect(() => {
    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Automatically set school for SCHOOL_ADMIN/TEACHER when adding new user.
  // Also re-pin role to STUDENT for a TEACHER specifically — the Select
  // that lets role drift away from STUDENT is only rendered for non-
  // teachers, but currentUser can still be null (still loading) on the
  // dialog's first render, so this closes the gap between "dialog opened"
  // and "we know the caller is a teacher".
  useEffect(() => {
    if (!addUserDialogOpen) return;
    if (isSchoolScopedRole(currentUser?.role) && currentUser?.school) {
      setNewUserData(prev => ({
        ...prev,
        school: currentUser.school
      }));
    }
    if (isTeacher(currentUser?.role)) {
      setNewUserData(prev => ({ ...prev, role: 'STUDENT' }));
    }
  }, [addUserDialogOpen, currentUser]);

  const fetchDashboardData = async (user = null) => {
    const userToCheck = user || currentUser;
    setDashboardError(null);
    try {
      // Fetch dashboard stats
      const statsResponse = await authFetch(`${config.API_BASE_URL}/api/dashboard/stats`);
      const statsData = await handleApiResponse(statsResponse, navigate);
      if (statsData === null) return; // 401 — handleApiResponse already redirected
      setStats(statsData);

      // Fetch users
      const usersResponse = await authFetch(`${config.API_BASE_URL}/api/user-access`);
      const usersData = await handleApiResponse(usersResponse, navigate);
      if (usersData === null) return;
      setUsers(usersData);

      // Fetch groups
      const groupsResponse = await authFetch(`${config.API_BASE_URL}/api/groups`);
      const groupsData = await handleApiResponse(groupsResponse, navigate);
      if (groupsData === null) return;
      setGroups(groupsData);
    } catch (error) {
      // Surfaced as a banner (see dashboardError) so a failed load never
      // looks the same as "this school has no users or classes yet".
      console.error('Error fetching dashboard data:', error);
      setDashboardError(`Couldn't load your school's data. ${describeApiError(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await authFetch(`${config.API_BASE_URL}/api/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole })
      });
      const data = await handleApiResponse(response, navigate);
      if (data === null) return; // 401 — already redirected

      // Update the local state
      setUsers(users.map(user => {
        if (user.id === userId) {
          const updatedUser = { ...user, role: newRole };
          // If user is promoted to MASTER_ADMIN, set school to "admin"
          if (newRole === 'MASTER_ADMIN') {
            updatedUser.school = 'admin';
          }
          return updatedUser;
        }
        return user;
      }));
      notify('Role updated.', 'success');
    } catch (error) {
      console.error('Error updating user role:', error);
      notify(`This role couldn't be updated. ${describeAdminError(error)}`, 'error');
    }
  };

  // `dropEnrollments` (Phase 4, admin console v2) — StudentDrawer's
  // TransferSchoolDialog "smart transfer check" opts into this after
  // previewing exactly which class enrolments it removes; every other
  // caller (AccountSection's plain SchoolPicker via StaffDrawer, the bulk
  // "Move school" dialog in StudentsPage.js) omits it, which is
  // byte-identical to the pre-Phase-4 body — enrolments are left alone.
  //
  // Returns `true` on a successful update, a falsy value on failure/401 (a
  // toast has already been shown either way) — TransferSchoolDialog awaits
  // this to decide whether it's safe to close itself; every other caller
  // ignores the return value exactly as before.
  const handleSchoolChange = async (userId, newSchool, { dropEnrollments = false } = {}) => {
    try {
      const response = await authFetch(`${config.API_BASE_URL}/api/users/${userId}/school`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ school: newSchool, drop_enrollments: dropEnrollments })
      });
      const data = await handleApiResponse(response, navigate);
      if (data === null) return false;

      // Update the local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, school: newSchool } : user
      ));
      notify(
        dropEnrollments && data.dropped_enrollments
          ? `School updated — removed from ${data.dropped_enrollments} class${data.dropped_enrollments === 1 ? '' : 'es'} at the old school.`
          : 'School updated.',
        'success'
      );
      return true;
    } catch (error) {
      console.error('Error updating user school:', error);
      notify(`This school couldn't be updated. ${describeApiError(error)}`, 'error');
      return false;
    }
  };

  const handleDeleteUser = async (user) => {
    if (!user) return;

    try {
      const response = await authFetch(`${config.API_BASE_URL}/api/users/${user.id}`, {
        method: 'DELETE',
      });
      const data = await handleApiResponse(response, navigate);
      if (data === null) return;

      // Remove user from local state
      setUsers(users.filter(u => u.id !== user.id));
      notify(`"${user.username}" was deleted.`, 'success');
    } catch (error) {
      console.error('Error deleting user:', error);
      notify(`This user couldn't be deleted. ${describeApiError(error)}`, 'error');
    }
  };

  const handleAddUser = async () => {
    setNewUserEmailError(null);
    try {
      // noEmail is a client-only escape hatch (see newUserData's comment
      // above) — never sent to the backend. An empty/whitespace-only email
      // is dropped the same way, so POST /api/users sees either a real
      // trimmed address or no "email" key at all, never "".
      const { noEmail, email, ...rest } = newUserData;
      const payload = { ...rest };
      const trimmedEmail = email.trim();
      if (!noEmail && trimmedEmail) {
        payload.email = trimmedEmail;
      }

      const response = await authFetch(`${config.API_BASE_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      const result = await handleApiResponse(response, navigate);
      if (result === null) return;

      // Add new user to local state
      setUsers([...users, result.user]);
      handleCloseAddUserDialog();
      notify('User created.', 'success');
    } catch (error) {
      console.error('Error creating user:', error);
      const message = describeAdminError(error);
      // A 400 here is only ever the email-format check (username/role/school
      // are all either pre-validated client-side or auto-derived) — route it
      // onto the Email field instead of a toast so it's obvious which field
      // to fix. Any other status still falls back to the toast.
      // NOTE: this /email/i sniff is coupled to the backend's current 400
      // message text — schemas.validate_email_format's "Invalid email
      // address." is the only email-mentioning 400 this endpoint returns
      // today. If that message ever changes to drop the word "email", this
      // silently stops routing to the field and falls back to the toast
      // instead (safe, just less precise) — not a hard break either way.
      if (error instanceof ApiError && error.status === 400 && /email/i.test(message)) {
        setNewUserEmailError(message);
      } else {
        notify(`This user couldn't be created. ${message}`, 'error');
      }
    }
  };

  // Generates a one-time temporary password — POST /api/users/{id}/reset-password.
  // Admin-only server-side. Also does not catch: StudentDrawer's
  // confirmAction flow calls this and shows the result (or a toast on
  // failure) itself — see performResetPassword there.
  const handleResetPassword = async (userId) => {
    const response = await authFetch(`${config.API_BASE_URL}/api/users/${userId}/reset-password`, {
      method: 'POST',
    });
    const data = await handleApiResponse(response, navigate);
    if (data === null) return null; // 401 — already redirected
    return data;
  };

  const handleNewUserChange = (field, value) => {
    // Convert school name to uppercase to prevent mixups
    const processedValue = field === 'school' ? value.toUpperCase() : value;
    if (field === 'email') setNewUserEmailError(null);
    setNewUserData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  // "No email for this user" checkbox — relaxes the Email field back to
  // optional and clears whatever was typed, so a stale draft address can
  // never be silently discarded/sent once the box is checked/unchecked.
  const handleNoEmailToggle = (checked) => {
    setNewUserEmailError(null);
    setNewUserData(prev => ({ ...prev, noEmail: checked, email: checked ? '' : prev.email }));
  };

  const handleCloseAddUserDialog = () => {
    setAddUserDialogOpen(false);
    setAddUserDialogTab(0);
    setBulkFile(null);
    setBulkPreview(null);
    setBulkUploading(false);
    setBulkSending(false);
    setNewUserData({ username: '', password: '', role: 'STUDENT', school: '', email: '', noEmail: false });
    setNewUserEmailError(null);
    setSingleInviteData({ email: '', role: 'STUDENT', school: '' });
    setSingleInviteSending(false);
    setSingleInviteResult(null);
  };

  // Same landing tab for every staff role, teacher included: "Single User"
  // is the fastest path (two fields, no waiting on email delivery), and a
  // teacher now has the same direct-create capability as an admin, just
  // capped to STUDENT — no reason for their dialog to default anywhere
  // else.
  const handleOpenAddUserDialog = () => {
    setAddUserDialogOpen(true);
    setAddUserDialogTab(0);
  };

  const handleSendSingleInvite = async () => {
    if (!singleInviteData.email) return;
    setSingleInviteSending(true);
    setSingleInviteResult(null);
    try {
      const payload = {
        email: singleInviteData.email.trim().toLowerCase(),
        role: singleInviteData.role,
        ...(!isSchoolScopedRole(currentUser?.role) && singleInviteData.school
          ? { school: singleInviteData.school.toUpperCase() }
          : {}),
      };
      const res = await authFetch(`${config.API_BASE_URL}/api/invitations/single`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const created = await handleApiResponse(res, navigate);
      if (created === null) return; // 401 — already redirected
      if (created?.email_status === 'FAILED') {
        setSingleInviteResult({ severity: 'error', message: `Invitation for ${singleInviteData.email} was saved, but email delivery is currently failing. Use Resend on the Email Invites page once delivery is restored, or contact support.` });
      } else if (created?.email_status === 'SKIPPED') {
        setSingleInviteResult({ severity: 'warning', message: `Invitation for ${singleInviteData.email} was created, but no email was sent because email delivery isn't configured on this server. The invite link can still be shared directly, or resend it once email delivery is configured.` });
      } else {
        setSingleInviteResult({ severity: 'success', message: `Invitation sent to ${singleInviteData.email}.` });
      }
      setSingleInviteData({ email: '', role: singleInviteData.role, school: singleInviteData.school });
    } catch (error) {
      setSingleInviteResult({ severity: 'error', message: describeAdminError(error, 'Failed to send invitation.') });
    } finally {
      setSingleInviteSending(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setTemplateDownloading(true);
    try {
      const resp = await authFetch(`${config.API_BASE_URL}/api/invitations/template`);
      if (resp.status === 401) {
        // Mirror handleApiResponse's own 401 handling — this response is
        // binary (.xlsx), so it can't be routed through handleApiResponse
        // itself, which always tries to parse a body.
        navigate('/signin');
        return;
      }
      if (!resp.ok) {
        let detail;
        try {
          const data = await resp.json();
          detail = data.detail;
        } catch (e) {
          // body wasn't JSON (e.g. an unhandled 500) — fall through to the generic message
        }
        throw new ApiError(resp.status, detail);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invite_template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      notify(`This template couldn't be downloaded. ${describeAdminError(error, 'Download failed')}`, 'error');
    } finally {
      setTemplateDownloading(false);
    }
  };

  const handleBulkFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkFile(file);
    setBulkPreview(null);
    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const url = new URL(`${config.API_BASE_URL}/api/invitations/upload`);
      if (isSchoolScopedRole(currentUser?.role) && currentUser?.school) {
        url.searchParams.set('school', currentUser.school);
      }
      const resp = await authFetch(url.toString(), {
        method: 'POST',
        body: formData,
      });
      const preview = await handleApiResponse(resp, navigate);
      if (preview === null) return;
      setBulkPreview(preview);
    } catch (error) {
      notify(`This file couldn't be uploaded. ${describeAdminError(error, 'Upload failed')}`, 'error');
      setBulkFile(null);
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkSendInvitations = async () => {
    if (!bulkPreview) return;
    const validRows = bulkPreview.rows.filter(r => r.row_status === 'VALID');
    if (validRows.length === 0) return;
    setBulkSending(true);
    try {
      const resp = await authFetch(`${config.API_BASE_URL}/api/invitations/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invitations: validRows.map(r => ({ email: r.email, role: r.role, school: r.school })),
        }),
      });
      const result = await handleApiResponse(resp, navigate);
      if (result === null) return; // 401 — already redirected
      const { total = 0, created = 0, sent = 0, results: rows = [] } = result;
      // A row with no invitation_id was never created at all — either it
      // duplicated another address in this same upload, or an equivalent
      // pending invitation already existed elsewhere. Those rows have
      // nothing to "Resend" on the Email Invites page, unlike a row that WAS
      // created but whose email delivery failed or was never attempted
      // (invitation_id is set either way). Rows created but not delivered
      // split further into two distinct, non-interchangeable outcomes:
      // deliveryFailed (SMTP was configured and rejected/erred — a real
      // failure) vs notConfigured (SMTP isn't configured on this server at
      // all — nothing was ever attempted, not a "success" and not a
      // "failure" in the usual sense).
      const skipped = rows.filter(r => r.invitation_id == null);
      const deliveryFailed = rows.filter(r => r.invitation_id != null && r.email_status === 'FAILED');
      const notConfigured = rows.filter(r => r.invitation_id != null && r.email_status === 'SKIPPED');
      if (sent === total && total > 0) {
        notify(`Done — ${sent} invitation${sent !== 1 ? 's' : ''} sent.`, 'success');
      } else if (sent === 0) {
        if (created > 0 && notConfigured.length === created) {
          // Every created row was created-but-unsent because SMTP isn't
          // configured on this server — nothing failed, nothing was even
          // attempted. This must not read like an error/outage.
          const skippedNote = skipped.length > 0
            ? ` ${skipped.length} other row${skipped.length !== 1 ? 's were' : ' was'} skipped as duplicate${skipped.length !== 1 ? 's' : ''} and not saved.`
            : '';
          notify(
            `${created} invitation${created !== 1 ? 's' : ''} created, but no email was sent because email delivery isn't configured on this server.${skippedNote} The invite link${created !== 1 ? 's can' : ' can'} still be shared directly, or resent once email delivery is configured.`,
            'warning'
          );
        } else if (created > 0) {
          // Rows were saved, but email delivery is failing for all of them —
          // an infra issue the admin can't fix here, but they can retry later.
          const skippedNote = skipped.length > 0
            ? ` ${skipped.length} other row${skipped.length !== 1 ? 's were' : ' was'} skipped as duplicate${skipped.length !== 1 ? 's' : ''} and not saved.`
            : '';
          const notConfiguredNote = notConfigured.length > 0
            ? ` ${notConfigured.length} of those had no email sent at all because delivery isn't configured on this server.`
            : '';
          notify(
            `${created} invitation${created !== 1 ? 's' : ''} saved, but email delivery is currently failing.${notConfiguredNote}${skippedNote} Use Resend on the Email Invites page once delivery is restored, or contact support.`,
            'error'
          );
        } else {
          // Nothing was created at all (e.g. every address already had a
          // live pending invite) — there's nothing to see on the
          // Email Invites page, so point at what actually went wrong instead.
          const reasons = rows
            .filter(r => !r.success)
            .slice(0, 3)
            .map(r => (r.error ? `${r.email} — ${r.error}` : r.email))
            .join('; ');
          notify(
            `No invitations were created.${reasons ? ` ${reasons}.` : ''} Fix the rows and try again.`,
            'error'
          );
        }
      } else {
        const failedEmails = deliveryFailed.map(r => r.email);
        const shown = failedEmails.slice(0, 3);
        const remaining = failedEmails.length - shown.length;
        const failedList = shown.join(', ') + (remaining > 0 ? `, +${remaining} more` : '');
        const deliveryPart = deliveryFailed.length > 0
          ? ` ${deliveryFailed.length} failed to deliver${failedList ? ` (${failedList})` : ''} — use Resend on the Email Invites page once delivery is restored, or contact support.`
          : '';
        const notConfiguredPart = notConfigured.length > 0
          ? ` ${notConfigured.length} invitation${notConfigured.length !== 1 ? 's were' : ' was'} created but no email sent because delivery isn't configured on this server.`
          : '';
        const skippedPart = skipped.length > 0
          ? ` ${skipped.length} address${skipped.length !== 1 ? 'es were' : ' was'} skipped as duplicate${skipped.length !== 1 ? 's' : ''} — nothing to resend, they already have a pending invitation.`
          : '';
        notify(
          `${sent} of ${total} invitation${total !== 1 ? 's' : ''} delivered.${deliveryPart}${notConfiguredPart}${skippedPart}`,
          'warning'
        );
      }
      handleCloseAddUserDialog();
    } catch (error) {
      notify(`Some invitations couldn't be sent. ${describeAdminError(error, 'Send failed')}`, 'error');
    } finally {
      setBulkSending(false);
    }
  };

  const handleAccessToggle = async (userId, currentAccess) => {
    // If revoking access, confirm first — this immediately signs the user
    // out and blocks them from the platform.
    if (currentAccess) {
      const user = users.find(u => u.id === userId);
      const name = user?.username || 'This user';
      confirmAction({
        title: 'Revoke Platform Access',
        body: `${name} will immediately lose access to LearnAndMasterAI. They'll be signed out (if currently logged in) and blocked from signing back in until access is granted again.`,
        confirmLabel: 'Revoke Access',
        onConfirm: () => performAccessToggle(userId, currentAccess),
      });
      return;
    }

    // If granting access, proceed directly
    await performAccessToggle(userId, currentAccess);
  };

  const performAccessToggle = async (userId, currentAccess) => {
    try {
      const response = await authFetch(`${config.API_BASE_URL}/api/users/${userId}/access`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_granted: !currentAccess })
      });
      const data = await handleApiResponse(response, navigate);
      if (data === null) return; // 401 — already redirected

      // Update the local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, access_granted: !currentAccess } : user
      ));
      notify(`Access ${!currentAccess ? 'granted' : 'revoked'}.`, 'success');
    } catch (error) {
      console.error('Error updating user access:', error);
      notify(`This access change couldn't be saved. ${describeAdminError(error)}`, 'error');
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Collapsing the nav rail is a desktop-only affordance: the mobile Drawer
  // is a temporary overlay the admin explicitly opened to navigate, so
  // shrinking it to icons-only would only make it harder to use for no
  // benefit — there's no persistent width to reclaim on a phone. Mirrors
  // ChatSidebar's collapsed rail being gated the same way (see
  // components/sidebar/ChatSidebar.js).
  const railCollapsed = navCollapsed && !isMobile;
  const effectiveDrawerWidth = railCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  const handleToggleNavCollapsed = () => setNavCollapsed((prev) => !prev);

  useEffect(() => {
    try {
      window.localStorage.setItem(NAV_COLLAPSED_STORAGE_KEY, navCollapsed ? '1' : '0');
    } catch {
      // Storage unavailable — collapse still works for this session, it
      // just won't survive a reload.
    }
  }, [navCollapsed]);

  // Help & Guides View (for MASTER_ADMIN, SCHOOL_ADMIN, and TEACHER)
  const PlatformGuideView = () => {
    const availableGuides = [
      {
        title: 'Master Admin Guide',
        description: 'Complete guide for Master Administrators covering user management, class creation, and system administration.',
        filename: 'master-admin-guide.html',
        roles: ['MASTER_ADMIN'],
        color: ROLE_COLORS[ROLES.MASTER_ADMIN]
      },
      {
        title: 'School Admin Guide',
        description: 'Comprehensive guide for School Administrators on managing users within their school.',
        filename: 'school-admin-guide.html',
        roles: ['MASTER_ADMIN', 'SCHOOL_ADMIN'],
        color: ROLE_COLORS[ROLES.SCHOOL_ADMIN]
      },
      {
        title: 'Teacher Guide',
        description: 'Guide for teachers on managing their classes, rosters, curriculum, and inviting students.',
        filename: 'teacher-guide.html',
        roles: ['MASTER_ADMIN', 'SCHOOL_ADMIN', 'TEACHER'],
        color: ROLE_COLORS[ROLES.TEACHER]
      },
      {
        title: 'Student Guide',
        description: 'Guide for students on how to use the platform features and participate in classes.',
        filename: 'student-guide.html',
        roles: ['MASTER_ADMIN', 'SCHOOL_ADMIN', 'TEACHER'],
        color: ROLE_COLORS[ROLES.STUDENT]
      }
    ];

    const filteredGuides = availableGuides.filter(guide =>
      guide.roles.includes(currentUser?.role)
    );

    const openGuide = (filename) => {
      window.open(`/guides/${filename}`, '_blank');
    };

    return (
      <Box sx={{ p: 3, backgroundColor: '#f7f7f8', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#18181b', fontWeight: 600, mb: 3 }}>
          Help & Guides
        </Typography>

        <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
          Access comprehensive guides to help you navigate and use the platform effectively.
          Click on any guide below to open it in a new tab.
        </Typography>

        <Grid container spacing={3}>
          {filteredGuides.map((guide, index) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: radius.surface,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                  }
                }}
                onClick={() => openGuide(guide.filename)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MenuBookIcon
                      sx={{
                        color: guide.color,
                        fontSize: 32,
                        mr: 2
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#18181b'
                      }}
                    >
                      {guide.title}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      lineHeight: 1.6,
                      mb: 3
                    }}
                  >
                    {guide.description}
                  </Typography>

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: guide.color,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: guide.color,
                        opacity: 0.9
                      }
                    }}
                  >
                    Open Guide
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredGuides.length === 0 && (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No guides available for your role
            </Typography>
            <Typography color="textSecondary" sx={{ mt: 1 }}>
              Contact your administrator for access to platform guides
            </Typography>
          </Card>
        )}
      </Box>
    );
  };

  // Placeholder views for other sections
  const PlaceholderView = ({ title, description }) => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h4" gutterBottom>
        {title}
      </Typography>
      <Typography color="textSecondary">
        {description}
      </Typography>
    </Box>
  );

  // Descendant <Routes> — this component is mounted at /admin/* (see
  // App.js), and every path below is relative to that, resolved
  // automatically against the ancestor <Route path="/admin/*">'s matched
  // prefix (React Router's "descendant routes" pattern — a second <Routes>
  // rendered inside a route's element, not via <Outlet>).
  //
  // `:username?`/`:groupId?` intentionally share their parent view's
  // element rather than getting a second <Route> — StudentsPage/ClassesPage
  // render the same list either way, just with a drawer/detail pane open
  // when the param is present. `:flagId` on the safety routes is the one
  // exception: SafetyReviewPage is a genuinely different screen from the
  // triage list SafetyFlagsView renders, so it gets its own <Route> instead.
  const renderRoutes = () => (
    <Routes>
      <Route
        index
        element={<OverviewPage stats={stats} loading={loading} currentUser={currentUser} />}
      />
      <Route
        path="students/:username?"
        element={
          <StudentsPage
            groups={groups}
            onRoleChange={handleRoleChange}
            onSchoolChange={handleSchoolChange}
            onDeleteUser={handleDeleteUser}
            onAccessToggle={handleAccessToggle}
            onResetPassword={handleResetPassword}
            onOpenAddUserDialog={handleOpenAddUserDialog}
            currentUserRole={currentUser?.role}
            currentUser={currentUser}
            notify={notify}
            confirmAction={confirmAction}
          />
        }
      />
      <Route
        path="staff/:username?"
        element={
          // Route-level refusal, not just a hidden nav link (see
          // VIEW_ROUTES's `staff` comment) — a TEACHER hitting this path
          // directly (typed URL, stale bookmark, browser back into it) is
          // redirected to their Students list rather than ever mounting
          // StaffPage. `currentUser` can still be null on the very first
          // render (profile fetch in flight) — isTeacher(null) is false, so
          // this briefly renders StaffPage's loading state for every role
          // until /api/me resolves, same as every other role-gated view in
          // this file; StaffPage's own fetch is scoped server-side regardless
          // (see app/routes/admin.py's `roles` param), so nothing sensitive
          // is ever actually shown before the redirect can land.
          isTeacher(currentUser?.role) ? (
            <Navigate to="/admin/students" replace />
          ) : (
            <StaffPage
              currentUserRole={currentUser?.role}
              currentUser={currentUser}
              onRoleChange={handleRoleChange}
              onSchoolChange={handleSchoolChange}
              onDeleteUser={handleDeleteUser}
              onAccessToggle={handleAccessToggle}
              onResetPassword={handleResetPassword}
              onOpenAddUserDialog={handleOpenAddUserDialog}
              notify={notify}
              confirmAction={confirmAction}
            />
          )
        }
      />
      <Route
        path="classes/:groupId?"
        element={
          <ClassesPage
            groups={groups}
            onRefreshData={fetchDashboardData}
            currentUserRole={currentUser?.role}
            currentUser={currentUser}
            notify={notify}
            confirmAction={confirmAction}
          />
        }
      />
      {/* Class Rosters no longer has its own screen — Classes & Schedules'
          own detail pane (ClassesPage.js) grew teachers/time-slots/roster
          management for a class in one place, so the standalone page
          (ClassUserAssignmentView) was deleted. A stale bookmark/link still
          lands somewhere useful instead of 404ing or silently falling back
          to the dashboard — see VIEW_ROUTES' `assignments` comment. */}
      <Route path="rosters" element={<Navigate to="/admin/classes" replace />} />
      <Route
        path="invites"
        element={
          <InvitesPage
            currentUser={currentUser}
            notify={notify}
            confirmAction={confirmAction}
            onOpenAddUserDialog={handleOpenAddUserDialog}
          />
        }
      />
      {/* Sign-up Requests no longer has its own screen — folded into
          InvitesPage above (see VIEW_ROUTES' `accessRequests` comment). A
          stale bookmark/link still lands somewhere useful instead of 404ing
          or silently falling back to the dashboard. */}
      <Route path="requests" element={<Navigate to="/admin/invites" replace />} />
      <Route
        path="safety"
        element={<SafetyFlagsView currentUser={currentUser} />}
      />
      <Route
        path="safety/:flagId"
        element={<SafetyReviewPage currentUser={currentUser} notify={notify} onFlagsChanged={refreshSafetyFlagsCount} />}
      />
      <Route path="conversations" element={<ConversationViewer currentUser={currentUser} />} />
      <Route
        path="agents"
        element={
          // Agent Tutor is held behind a "Coming Soon" state for every role —
          // see AGENT_TUTOR_COMING_SOON in config.js. Flip that flag back to
          // false to restore this branch; AgentTutorView itself is untouched.
          // showHeading={false}: the AppBar above this content area already
          // renders "Agent Tutor" as the page title (menuItems label), so the
          // placeholder's own heading would visually repeat it right below —
          // stays in the DOM as an sr-only heading rather than disappearing.
          AGENT_TUTOR_COMING_SOON
            ? <AgentTutorComingSoon showHeading={false} />
            : <AgentTutorView currentUser={currentUser} groups={groups} />
        }
      />
      <Route path="guide" element={<PlatformGuideView />} />
      <Route path="curriculum" element={<CurriculumPage currentUser={currentUser} currentUserRole={currentUser?.role} />} />
      <Route
        path="schools"
        element={
          // Route-level refusal, not just a hidden nav link — a TEACHER
          // landing here directly (typed URL, stale bookmark) is redirected
          // to the dashboard rather than ever mounting SchoolManagement,
          // same pattern as the `staff` route above. MASTER_ADMIN and
          // SCHOOL_ADMIN both reach SchoolManagement itself: it renders the
          // full multi-school catalog for a master admin, but scopes a
          // school admin to a read-mostly view of their own school only
          // (timezone + schedule setup, no catalog/add/rename/merge) —
          // see that file's own header comment for the exact split.
          isTeacher(currentUser?.role) ? (
            <Navigate to="/admin" replace />
          ) : (
            <SchoolManagement currentUser={currentUser} />
          )
        }
      />
      {/* Unknown /admin/* path — same "fall back to the dashboard" behavior
          the old switch's `default` case had for an unrecognized
          currentView. */}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );

  const handleLogout = async () => {
    try {
      await authFetch(`${config.API_BASE_URL}/api/logout`, { method: "POST" });
    } catch (error) {
      // Even if the backend call fails (network hiccup, already-expired
      // session), still clear the local token and send the user to
      // sign-in — staying "logged in" client-side with a dead session is
      // worse than a failed logout request.
      console.error('Error logging out:', error);
    } finally {
      clearToken();
      navigate("/signin");
    }
  };

  // Sidebar content — rendered once for the mobile overlay Drawer (always
  // full, `rail=false`) and once for the desktop persistent Drawer (`rail`
  // follows the collapsed preference). A function, not a plain element,
  // specifically so the two call sites can pass different `rail` values —
  // see the two <Drawer> usages below.
  const renderDrawerContent = (rail) => (
    <Box sx={{
      background: `linear-gradient(180deg, ${adminColors.sidebar} 0%, #0e0f13 100%)`,
      color: 'white',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Toolbar
        sx={{
          minHeight: '64px !important',
          px: rail ? 1 : 2.5,
          display: 'flex',
          flexDirection: rail ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: rail ? 'center' : 'space-between',
          gap: rail ? 1 : 0,
          py: rail ? 1.5 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Box sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: '9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: rail ? 0 : 1.5,
            background: `linear-gradient(135deg, ${adminColors.accent}, ${adminColors.accentDark})`,
          }}>
            <SchoolIcon sx={{ color: 'white', fontSize: 19 }} />
          </Box>
          {!rail && (
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '1.05rem',
                letterSpacing: '-0.01em'
              }}
            >
              LearnAndMasterAI
            </Typography>
          )}
        </Box>
        {/* Desktop collapse/expand toggle — the genuinely new capability
            here (reclaims width for the wide Users/Invitations tables).
            aria-expanded reflects the *nav's* expanded state (not the
            button's own popup state, since this isn't a disclosure widget
            in the ARIA sense — it's a persistent layout toggle, same
            pattern as a sidebar-collapse control in any IDE). */}
        {!isMobile && (
          <Tooltip title={rail ? 'Expand navigation' : 'Collapse navigation'} placement="right">
            <IconButton
              size="small"
              onClick={handleToggleNavCollapsed}
              aria-label={rail ? 'Expand navigation' : 'Collapse navigation'}
              aria-expanded={!rail}
              aria-controls="admin-nav"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: 'white' },
                '&:focus-visible': { outline: `2px solid ${FOCUS_RING_ON_DARK}`, outlineOffset: 2 },
                ...TOUCH_HIT_SX,
              }}
            >
              {rail ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {/* Mobile overlay's explicit dismiss — mirrors ChatSidebar's "Close
            chat list" button (components/sidebar/ChatSidebar.js): relying on
            the scrim tap / Esc alone isn't discoverable as a way to close
            this drawer. This admin drawer previously had no such button. */}
        {isMobile && (
          <IconButton
            size="small"
            onClick={handleDrawerToggle}
            aria-label="Close navigation menu"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: 'white' },
              '&:focus-visible': { outline: `2px solid ${FOCUS_RING_ON_DARK}`, outlineOffset: 2 },
              ...TOUCH_HIT_SX,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Toolbar>
      <Divider sx={{ borderColor: adminColors.sidebarBorder }} />
      {/* overflowY: 'auto' (matching ChatSidebar.js's thread-list Box) is
          load-bearing, not decorative: without it, a flex item's automatic
          minimum height is its content's full height, so a long nav list
          (MASTER_ADMIN sees every group) grows this List taller than the
          280px-wide drawer's 100vh column instead of scrolling internally.
          That pushed the account footer below/at the Drawer paper's own
          overflow boundary, clipping it and the hover-reveal actions.
          Scoping the scroll to the nav list keeps the footer pinned. */}
      <List sx={{ py: 1.5, px: rail ? 1 : 1.25, flexGrow: 1, overflowY: 'auto' }}>
        {menuGroups.map((group, groupIndex) => (
          <Box key={group.id} component="section" aria-label={group.label}>
            {rail ? (
              // Group labels don't fit a 76px rail — a divider between
              // groups (skipped before the first) keeps the same visual
              // separation without truncated/rotated text.
              groupIndex > 0 && <Divider sx={{ borderColor: adminColors.sidebarBorder, my: 1 }} />
            ) : (
              <Typography
                variant="caption"
                component="h2"
                sx={{
                  display: 'block',
                  px: 1.5,
                  pt: groupIndex === 0 ? 0.25 : 2,
                  pb: 0.75,
                  // Matches the opacity already used for the profile-card role
                  // caption below (not a new, lower-contrast token) — dim
                  // relative to full-opacity nav labels, but still ~4.5:1+
                  // against the dark sidebar chrome.
                  color: 'rgba(255,255,255,0.5)',
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {group.label}
              </Typography>
            )}
            {group.items.map((item) => {
              // Accessible name a screen reader announces for this item
              // when collapsed to an icon — folds the pending-count badge
              // in as text since the rail replaces the numeric badge with
              // a plain dot (see below) that carries no text of its own.
              const railAccessibleName = item.badge
                ? `${item.label} — ${item.badge} pending`
                : item.label;
              const navButton = (
                <ListItemButton
                  component={Link}
                  to={viewHref(item.id)}
                  selected={currentView === item.id}
                  onClick={() => {
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }}
                  // Single source of truth for the accessible name: this
                  // explicit aria-label (present only in rail mode) always
                  // wins over the wrapping Tooltip's own aria-label
                  // injection below, because MUI's Tooltip clones its child
                  // with `{...tooltipProps, ...children.props}` — the
                  // child's own props are spread last and take precedence.
                  // Tooltip's `describeChild` is left at its default
                  // (false), so it never adds aria-describedby either — an
                  // AT reads this item's name exactly once, not as both a
                  // name and a duplicate description. In expanded mode this
                  // is explicitly `undefined` (not omitted) so it overrides
                  // Tooltip's injected aria-label too, leaving the
                  // accessible name to fall back to the visible
                  // ListItemText content, same as before this nav existed.
                  aria-label={rail ? railAccessibleName : undefined}
                  sx={{
                    ...TOUCH_HIT_SX,
                    borderRadius: '8px',
                    mb: 0.5,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    justifyContent: rail ? 'center' : 'flex-start',
                    px: rail ? 1 : 2,
                    color: currentView === item.id ? 'white' : 'rgba(255,255,255,0.72)',
                    backgroundColor: currentView === item.id ? adminColors.accent : 'transparent',
                    boxShadow: currentView === item.id ? '0 2px 8px rgba(79,70,229,0.35)' : 'none',
                    '&:hover': {
                      backgroundColor: currentView === item.id ? adminColors.accentDark : 'rgba(255,255,255,0.06)',
                      color: 'white',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${FOCUS_RING_ON_DARK}`,
                      outlineOffset: -2,
                    },
                    py: 1,
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: rail ? 'auto' : 38, justifyContent: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                  {/* Always mounted (not `{!rail && <ListItemText/>}`) so the
                      label cross-fades and collapses in step with the
                      280px->76px rail width transition instead of vanishing
                      in one commit while the row is still visually full
                      width, via a `grid-template-columns: 1fr -> 0fr`
                      "collapse toward zero via an intrinsic-size track"
                      idiom on the column axis. The label stays out of the
                      accessible-name computation in rail
                      mode regardless of being present in the DOM: the
                      ListItemButton's own explicit aria-label above takes
                      priority over content for any accessible name lookup. */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: rail ? '0fr' : '1fr',
                      minWidth: 0,
                      opacity: rail ? 0 : 1,
                      transition: theme.transitions.create(['grid-template-columns', 'opacity'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                  >
                    <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          m: 0,
                          whiteSpace: 'nowrap',
                          '& .MuiListItemText-primary': {
                            fontWeight: currentView === item.id ? 600 : 500,
                            fontSize: '0.9rem'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  {/* Pending-flags count on Safety Monitoring — omitted at 0 so
                      a clean queue doesn't carry a permanent, meaningless badge.
                      Solid error-red at all times (not a translucent-white
                      variant on the selected/indigo state) so the white label
                      keeps ~5:1 contrast whether or not the item is active.
                      Reads from theme.palette.error.main — adminTheme.js sets
                      that to the #dc2626 severity red (not MUI's d32f2f
                      default), so this and SafetyFlagsView's severity scale
                      are guaranteed to match via the one token, not a literal
                      that happens to agree with it today.
                      Collapsed to a plain dot in the rail (no room for a
                      2-digit count next to a 24px icon) — the exact number
                      still reaches screen readers via railAccessibleName
                      above, and is one click away in the full nav either way. */}
                  {!!item.badge && (
                    <Box
                      sx={rail ? {
                        position: 'absolute',
                        top: 6,
                        right: 14,
                        width: 8,
                        height: 8,
                        borderRadius: radius.pill,
                        backgroundColor: theme.palette.error.main,
                        flexShrink: 0,
                      } : {
                        ml: 1,
                        minWidth: 20,
                        height: 20,
                        px: 0.75,
                        borderRadius: radius.pill,
                        backgroundColor: theme.palette.error.main,
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {rail ? null : item.badge}
                    </Box>
                  )}
                </ListItemButton>
              );
              // Always the same wrapper type/key at this position — never a
              // <Tooltip> in rail mode and a bare <React.Fragment> otherwise
              // (that used to flip the element type on every toggle, which
              // makes React tear down and recreate the ListItemButton
              // instead of just restyling it: the 0.15s hover/selected
              // transition can't play on the first interaction after a
              // toggle, :focus-visible is dropped, and focus inside the
              // item would fall back to <body> the moment collapse is
              // triggered any way other than the toggle button itself
              // stealing focus first, e.g. a future keyboard shortcut or a
              // resize-triggered auto-collapse). Disabling the listeners
              // when not `rail` keeps the popup from ever opening in
              // expanded mode without unmounting the wrapper.
              return (
                <Tooltip
                  key={item.id}
                  title={railAccessibleName}
                  placement="right"
                  disableHoverListener={!rail}
                  disableFocusListener={!rail}
                  disableTouchListener={!rail}
                >
                  {navButton}
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </List>

      {/* User profile section at bottom — avatar size/gradient/initials
          (getInitials, from utils/userDisplay.js) now match ChatSidebar's
          account pill exactly, rather than each surface rolling its own
          (this one used to show only the first raw character and a bigger
          36px circle). Role text now goes through the same ROLE_LABELS map
          chat uses too, so "MASTER_ADMIN" reads as "Master Admin" here
          exactly like it does in the chat sidebar's account menu. */}
      <Divider sx={{ borderColor: adminColors.sidebarBorder }} />
      {rail ? (
        // Collapsed rail: same tappable-avatar-opens-a-menu pattern as
        // ChatSidebar's own collapsed rail (components/sidebar/ChatSidebar.js)
        // — there's no room for the name/role/hover-actions block below, so
        // "Chat" and "Sign Out" move into a click-to-open menu instead.
        <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={currentUser?.username || 'Account'} placement="right">
            <Box
              role="button"
              tabIndex={0}
              aria-label={`Account menu for ${currentUser?.username || 'Account'}`}
              aria-haspopup="true"
              aria-expanded={Boolean(railUserMenuAnchor)}
              onClick={(e) => setRailUserMenuAnchor(e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setRailUserMenuAnchor(e.currentTarget);
                }
              }}
              sx={{
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                cursor: 'pointer',
                '&:focus-visible': {
                  outline: `2px solid ${FOCUS_RING_ON_DARK}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: `linear-gradient(135deg, ${adminColors.accent}, ${adminColors.accentDark})`,
                  fontSize: '0.8rem',
                  fontWeight: 700,
                }}
              >
                {getInitials(currentUser?.username)}
              </Avatar>
            </Box>
          </Tooltip>
          <Menu
            anchorEl={railUserMenuAnchor}
            open={Boolean(railUserMenuAnchor)}
            onClose={() => setRailUserMenuAnchor(null)}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          >
            <MenuItem onClick={() => { setRailUserMenuAnchor(null); navigate('/chat'); }}>
              <ListItemIcon>
                <ChatIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Chat</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setRailUserMenuAnchor(null); handleLogout(); }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign Out</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                background: `linear-gradient(135deg, ${adminColors.accent}, ${adminColors.accentDark})`,
                fontSize: '0.8rem',
                fontWeight: 700,
                mr: 1.5
              }}
            >
              {getInitials(currentUser?.username)}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', lineHeight: 1.3 }} noWrap>
                {currentUser?.username || 'Admin'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.2 }} noWrap>
                {ROLE_LABELS[currentUser?.role] || currentUser?.role || 'Admin'}
              </Typography>
            </Box>
          </Box>
          {/* Always-on account actions — previously revealed on hover/
              focus (or forced open on isMobile only), now a permanent part
              of the footer's designed layout on every breakpoint, matching
              ChatSidebar.js's own account footer. Sign Out rests muted
              (same weight as Chat) and only goes red on hover/focus — at
              full-time visibility, resting red would out-compete its
              neighbour and read as more urgent than a reversible action
              warrants; the color is reserved for the moment intent is
              actually signaled. */}
          <Box className="sidebar-actions" sx={{ display: 'flex', gap: 1, marginTop: '12px' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ChatIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/chat')}
              sx={{
                ...TOUCH_HIT_SX,
                flex: 1,
                color: 'rgba(255,255,255,0.75)',
                borderColor: adminColors.sidebarBorder,
                textTransform: 'none',
                fontSize: '0.8rem',
                py: 0.5,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.3)',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  color: 'white',
                }
              }}
            >
              Chat
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
              onClick={handleLogout}
              sx={{
                ...TOUCH_HIT_SX,
                flex: 1,
                // Resting state matches Chat's muted weight — red is
                // reserved for hover/focus-visible below (see comment
                // above). theme.palette.error.light (not .main): the
                // sidebar chrome is dark, so this uses the lighter red
                // companion for legibility, same #f87171 as before but
                // now sourced from the token.
                color: 'rgba(255,255,255,0.75)',
                borderColor: adminColors.sidebarBorder,
                textTransform: 'none',
                fontSize: '0.8rem',
                py: 0.5,
                '&:hover': {
                  color: theme.palette.error.light,
                  borderColor: theme.palette.error.light,
                  backgroundColor: alpha(theme.palette.error.light, 0.08),
                },
                '&:focus-visible': {
                  color: theme.palette.error.light,
                  borderColor: theme.palette.error.light,
                  backgroundColor: alpha(theme.palette.error.light, 0.08),
                }
              }}
            >
              Sign Out
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  if (loading) {
    // Skeleton mirrors the app shell + dashboard overview (lead safety
    // tile, 3-up KPI row, two tables) instead of a bare spinner, so the
    // transition into the real layout doesn't jump-cut. currentUser isn't
    // loaded yet at this point, so the sidebar skeleton is chrome-only —
    // it can't know which nav groups this user will see.
    const skeletonMotionSx = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };
    return (
      <>
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: adminColors.surface }}>
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              width: effectiveDrawerWidth,
              flexShrink: 0,
              p: railCollapsed ? 1.5 : 2.5,
              background: `linear-gradient(180deg, ${adminColors.sidebar} 0%, #0e0f13 100%)`,
              transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {railCollapsed ? (
              [0, 1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" width={38} height={38} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '8px', mb: 1, mx: 'auto', ...skeletonMotionSx }} />
              ))
            ) : (
              <>
                <Skeleton variant="rounded" width={150} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.08)', mb: 4, ...skeletonMotionSx }} />
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rounded" height={38} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '8px', mb: 1, ...skeletonMotionSx }} />
                ))}
              </>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, sm: 3 } }}>
            <Skeleton variant="rounded" width={220} height={32} sx={{ mb: 3, ...skeletonMotionSx }} />
            <Skeleton variant="rounded" height={112} sx={{ mb: 3, borderRadius: '12px', ...skeletonMotionSx }} />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[1, 2, 3].map((i) => (
                <Grid size={{ xs: 12, sm: 4 }} key={i}>
                  <Skeleton variant="rounded" height={76} sx={{ borderRadius: '12px', ...skeletonMotionSx }} />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={3}>
              {[1, 2].map((i) => (
                <Grid size={{ xs: 12, md: 6 }} key={i}>
                  <Skeleton variant="rounded" height={220} sx={{ borderRadius: '12px', ...skeletonMotionSx }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
        {/* Mounted here too (not just in the loaded branch below) so
            notify()/confirmAction() calls that can fire during initial load
            (e.g. from fetchDashboardData's error handling) aren't silent
            no-ops - see ConfirmHost/NotificationHost above. */}
        <ConfirmHost apiRef={confirmApiRef} />
        <NotificationHost apiRef={notifyApiRef} />
      </>
    );
  }

  if (meError) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        px: 3,
        backgroundColor: adminColors.surface
      }}>
        <Alert
          severity="error"
          sx={{ maxWidth: 480 }}
          action={
            <Button color="inherit" size="small" onClick={() => { setLoading(true); fetchCurrentUser(); }}>
              Retry
            </Button>
          }
        >
          {meError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', backgroundColor: adminColors.surface, minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { md: `${effectiveDrawerWidth}px` },
          backgroundColor: adminColors.surfaceRaised,
          color: adminColors.ink,
          borderBottom: '1px solid rgba(24,24,27,0.08)',
          boxShadow: 'none',
          transition: theme.transitions.create(['width', 'margin'], { duration: theme.transitions.duration.shorter }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: '-0.01em' }}>
            {menuItems.find(item => item.id === currentView)?.label || 'Dashboard'}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        id="admin-nav"
        sx={{
          width: { md: effectiveDrawerWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {/* Mobile drawer — always renders the full nav (rail=false): it's a
            temporary overlay, not the persistent column the collapse
            preference applies to. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH_EXPANDED,
              backgroundColor: adminColors.sidebar,
              border: 'none',
            },
          }}
        >
          {renderDrawerContent(false)}
        </Drawer>

        {/* Desktop drawer — width and content follow the collapsed
            preference. */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: effectiveDrawerWidth,
              backgroundColor: adminColors.sidebar,
              border: 'none',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            },
          }}
          open
        >
          {renderDrawerContent(railCollapsed)}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { md: `calc(100% - ${effectiveDrawerWidth}px)` },
          backgroundColor: adminColors.surface,
          minHeight: '100vh',
          transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Toolbar />
        {dashboardError && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => { setLoading(true); fetchDashboardData(); }}>
                  Retry
                </Button>
              }
            >
              {dashboardError}
            </Alert>
          </Box>
        )}
        {renderRoutes()}
      </Box>

      {/* Add User Dialog */}
      <Dialog
        open={addUserDialogOpen}
        onClose={handleCloseAddUserDialog}
        aria-labelledby="add-user-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="add-user-dialog-title">Add New User</DialogTitle>

        <Tabs
          value={addUserDialogTab}
          onChange={(_, v) => setAddUserDialogTab(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {/* Each Tab carries an explicit `value` (its semantic tab id, not
              its position). A teacher can create a student account directly
              here (capped to STUDENT by assignableRoles + assert_role_assignable
              server-side), same as an admin — the role/school fields below
              are what actually restrict what a teacher can submit, not tab
              visibility. */}
          <Tab value={0} label="Single User" />
          <Tab value={1} label="Bulk Invite via Excel" />
          <Tab value={2} label="Email Invite" />
        </Tabs>

        <DialogContent>
          {/* TAB 0: single-user form */}
          {addUserDialogTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Username"
                value={newUserData.username}
                onChange={(e) => handleNewUserChange('username', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Password"
                type="password"
                value={newUserData.password}
                onChange={(e) => handleNewUserChange('password', e.target.value)}
                fullWidth
                required
              />
              {/* Required by default so new users can use self-service
                  password reset — the "No email" checkbox below is the
                  explicit escape hatch for test/bulk accounts. Format is
                  validated client-side (EMAIL_FORMAT_RE, mirroring the
                  server's schemas.EMAIL_FORMAT_RE); the server's own 400 is
                  still surfaced here via newUserEmailError as a fallback. */}
              <TextField
                label="Email"
                type="email"
                value={newUserData.email}
                onChange={(e) => handleNewUserChange('email', e.target.value)}
                fullWidth
                required={!newUserData.noEmail}
                disabled={newUserData.noEmail}
                placeholder="name@school.edu"
                error={Boolean(newUserEmailError) || (!newUserData.noEmail && newUserData.email.trim() !== '' && !EMAIL_FORMAT_RE.test(newUserData.email.trim()))}
                helperText={
                  newUserData.noEmail
                    ? "Skipped — this account won't have self-service password reset."
                    : newUserEmailError
                      || (newUserData.email.trim() !== '' && !EMAIL_FORMAT_RE.test(newUserData.email.trim())
                        ? 'Enter a valid email address.'
                        : 'Lets this user reset their own password by email.')
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newUserData.noEmail}
                    onChange={(e) => handleNoEmailToggle(e.target.checked)}
                    size="small"
                  />
                }
                label="No email for this user (young student, shared account, or test account)"
                sx={{ mt: -1, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: '#52525b' } }}
              />
              {/* A teacher can only ever create a STUDENT (assignableRoles
                  caps them there, and the server re-asserts it), so this is
                  a locked field, not a single-item dropdown — no affordance
                  that suggests a choice that doesn't exist. */}
              {isTeacher(currentUser?.role) ? (
                <TextField
                  label="Role"
                  value="Student"
                  fullWidth
                  disabled
                  helperText="Teachers can only add students directly."
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newUserData.role}
                    onChange={(e) => handleNewUserChange('role', e.target.value)}
                    label="Role"
                  >
                    <MenuItem value="STUDENT">Student</MenuItem>
                    {assignableRoles(currentUser?.role).includes(ROLES.TEACHER) && (
                      <MenuItem value="TEACHER">Teacher</MenuItem>
                    )}
                    {assignableRoles(currentUser?.role).includes(ROLES.SCHOOL_ADMIN) && (
                      <MenuItem value="SCHOOL_ADMIN">School Admin</MenuItem>
                    )}
                    {assignableRoles(currentUser?.role).includes(ROLES.MASTER_ADMIN) && (
                      <MenuItem value="MASTER_ADMIN">Master Admin</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}
              <TextField
                label="School"
                value={newUserData.school}
                onChange={(e) => handleNewUserChange('school', e.target.value)}
                fullWidth
                placeholder="Enter school name"
                disabled={isSchoolScopedRole(currentUser?.role)}
                helperText={isSchoolScopedRole(currentUser?.role) ? 'Scoped to your school automatically.' : ' '}
              />
            </Box>
          )}

          {/* TAB 2: single email invite (LEAR-26) */}
          {addUserDialogTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Send an email invitation. The recipient will receive a secure link to
                create their own username and password. Their account will still
                require admin approval before they can sign in.
              </Typography>
              {singleInviteResult && (
                <Alert severity={singleInviteResult.severity} onClose={() => setSingleInviteResult(null)}>
                  {singleInviteResult.message}
                </Alert>
              )}
              <TextField
                label="Email address"
                type="email"
                value={singleInviteData.email}
                onChange={(e) => setSingleInviteData(p => ({ ...p, email: e.target.value }))}
                fullWidth
                required
                placeholder="student@school.edu"
              />
              {/* Same reasoning as the Single User tab's Role field: for a
                  teacher, assignableRoles caps this to STUDENT alone, so a
                  live Select would collapse to exactly one selectable item.
                  A locked field, not a single-item dropdown — no affordance
                  that suggests a choice that doesn't exist. */}
              {isTeacher(currentUser?.role) ? (
                <TextField
                  label="Role"
                  value="Student"
                  fullWidth
                  disabled
                  helperText="Teachers can only invite students directly."
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={singleInviteData.role}
                    onChange={(e) => setSingleInviteData(p => ({ ...p, role: e.target.value }))}
                    label="Role"
                  >
                    <MenuItem value="STUDENT">Student</MenuItem>
                    {assignableRoles(currentUser?.role).includes(ROLES.TEACHER) && (
                      <MenuItem value="TEACHER">Teacher</MenuItem>
                    )}
                    {assignableRoles(currentUser?.role).includes(ROLES.SCHOOL_ADMIN) && (
                      <MenuItem value="SCHOOL_ADMIN">School Admin</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}
              <TextField
                label="School"
                value={isSchoolScopedRole(currentUser?.role) ? (currentUser?.school || '') : singleInviteData.school}
                onChange={(e) => setSingleInviteData(p => ({ ...p, school: e.target.value.toUpperCase() }))}
                fullWidth
                // Only mark this required once we actually know the caller's
                // role — currentUser is null while it's still loading, and
                // `!isSchoolScopedRole(...)` is true for null, so without
                // this check a school-scoped user briefly sees "School"
                // flagged required before their own role loads in.
                required={currentUser != null && !isSchoolScopedRole(currentUser.role)}
                placeholder="Enter school name"
                disabled={isSchoolScopedRole(currentUser?.role)}
                helperText={
                  isSchoolScopedRole(currentUser?.role)
                    ? 'Scoped to your school automatically.'
                    : (currentUser != null ? 'Required.' : ' ')
                }
              />
            </Box>
          )}

          {/* TAB 1: bulk Excel invite */}
          {addUserDialogTab === 1 && (
            <Box sx={{ mt: 2 }}>
              {!bulkPreview && (
                <Box sx={{ textAlign: 'center', p: 3, border: '2px dashed #ccc', borderRadius: radius.surface }}>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    Upload an Excel (.xlsx) file
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Required columns: <strong>email</strong>, <strong>role</strong> ({assignableRoles(currentUser?.role).join(' / ')})
                    {!isSchoolScopedRole(currentUser?.role) && <>, <strong>school</strong></>}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      disabled={bulkUploading}
                    >
                      Choose File
                      <input type="file" accept=".xlsx" hidden onChange={handleBulkFileSelect} />
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={templateDownloading}
                      onClick={handleDownloadTemplate}
                    >
                      Download Excel Template
                    </Button>
                  </Box>
                  {bulkUploading && <LinearProgress sx={{ mt: 2 }} />}
                </Box>
              )}

              {bulkPreview && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip
                      label={`${bulkPreview.valid_rows} valid`}
                      color="success"
                      icon={<CheckCircleIcon />}
                      size="small"
                    />
                    <Chip
                      label={`${bulkPreview.invalid_rows} invalid`}
                      color={bulkPreview.invalid_rows > 0 ? 'error' : 'default'}
                      icon={<ErrorIcon />}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      {bulkPreview.original_filename}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => { setBulkFile(null); setBulkPreview(null); }}
                    >
                      Change file
                    </Button>
                  </Box>
                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>School</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkPreview.rows.map(row => (
                          <TableRow
                            key={row.row_number}
                            sx={{ bgcolor: row.row_status === 'VALID' ? 'success.50' : 'error.50' }}
                          >
                            <TableCell>{row.row_number}</TableCell>
                            <TableCell>{row.email || '—'}</TableCell>
                            <TableCell>{row.role || '—'}</TableCell>
                            <TableCell>{row.school || '—'}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={row.row_status}
                                color={row.row_status === 'VALID' ? 'success' : 'error'}
                                title={row.error_reason || ''}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseAddUserDialog}>Cancel</Button>
          {addUserDialogTab === 0 && (
            <Button
              onClick={handleAddUser}
              color="primary"
              variant="contained"
              disabled={
                !newUserData.username ||
                !newUserData.password ||
                (!newUserData.noEmail && !newUserData.email.trim()) ||
                (newUserData.email.trim() !== '' && !EMAIL_FORMAT_RE.test(newUserData.email.trim()))
              }
            >
              Create User
            </Button>
          )}
          {addUserDialogTab === 1 && bulkPreview && bulkPreview.valid_rows > 0 && (
            <Button
              onClick={handleBulkSendInvitations}
              color="primary"
              variant="contained"
              disabled={bulkSending}
            >
              {bulkSending && <CircularProgress size={18} sx={{ mr: 1 }} />}
              Send {bulkPreview.valid_rows} Invitation{bulkPreview.valid_rows !== 1 ? 's' : ''}
            </Button>
          )}
          {addUserDialogTab === 2 && (() => {
            const roleStillLoading = currentUser == null;
            const missingEmail = !singleInviteData.email;
            // Mirror the School field's own `required` guard: while
            // currentUser is still loading we don't yet know whether the
            // caller is school-scoped (school auto-filled) or not (school
            // required), so don't flag the field as missing yet — same
            // reasoning that keeps the field's asterisk from flashing.
            const missingSchool = !roleStillLoading && !isSchoolScopedRole(currentUser.role) && !singleInviteData.school;
            const isDisabled = singleInviteSending || roleStillLoading || missingEmail || missingSchool;
            const disabledReason = singleInviteSending
              ? ''
              : roleStillLoading
                ? 'Loading your role…'
                : missingEmail
                  ? 'Enter an email address to continue.'
                  : missingSchool
                    ? 'Enter a school to continue.'
                    : '';
            return (
              <Tooltip title={disabledReason} placement="top" disableHoverListener={!disabledReason}>
                {/* Span wrapper: disabled buttons don't fire the pointer
                    events Tooltip listens for. */}
                <span>
                  <Button
                    onClick={handleSendSingleInvite}
                    color="primary"
                    variant="contained"
                    disabled={isDisabled}
                  >
                    {singleInviteSending && <CircularProgress size={18} sx={{ mr: 1 }} />}
                    Send Invitation
                  </Button>
                </span>
              </Tooltip>
            );
          })()}
        </DialogActions>
      </Dialog>

      {/* Shared destructive-action confirm dialog — see confirmAction() above.
          Used for delete user, delete class, revoke access, cancel
          invitation, and any future "are you sure" prompt in this file.
          State lives inside ConfirmHost, not AdminDashboardV2, so opening it
          doesn't remount the active view. */}
      <ConfirmHost apiRef={confirmApiRef} />

      {/* Toast notifications — see notify() above, replaces window.alert().
          State lives inside NotificationHost, not AdminDashboardV2, so
          firing a toast doesn't remount the active view. */}
      <NotificationHost apiRef={notifyApiRef} />
    </Box>
  );
}

export default AdminDashboardV2;
